import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { UrlService } from 'app/core/http/url.service';
import { ModalService } from 'app/core/modals/services/modal.service';
import { ClassInfo, UpdatedClassInfo } from 'app/teacher/models/class-info';
import { ClassStudentModel } from 'app/teacher/models/class-student.model';
import { PremiumSubscriptionsInfo } from 'app/teacher/models/premium-subscriptions-info.model';
import { ClassesService } from 'app/teacher/services/classes.service';
import { TeacherDataService } from 'app/teacher/services/teacher-data.service';
import { RenewClassModalComponent } from '../modals/renew-class-modal/renew-class-modal.component';

@Injectable({
  providedIn: 'root'
})
export class TeacherService {
  public hideParentPanel = false;
  private classInfo = new BehaviorSubject<ClassInfo | null>(null);
  public classInfoObs = this.classInfo as Observable<ClassInfo | null>;

  private classId = new BehaviorSubject<string>('');
  public classIdObs = this.classId.asObservable();

  public constructor(
    private readonly httpClient: HttpClient,
    private readonly urlService: UrlService,
    private readonly dataService: TeacherDataService,
    private readonly modalService: ModalService,
    private readonly classesService: ClassesService) { }

  public get activeClassId(): string {
    return this.classId.value;
  }

  public set activeClassId(classId: string) {
    this.classId.next(classId);
  }

  public get activeClassInfo(): ClassInfo | null {
    return this.classInfo.value;
  }

  public getClassInfo(classId: string, avoidSaving = false): Observable<ClassInfo> {
    return this.dataService.getClassInfo(classId).pipe(
      tap(classInfo => !avoidSaving && this.classInfo.next(classInfo))
    );
  }

  public allowUntracked(classId: string, allowUntracked: boolean): Observable<unknown> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'Class', classId, 'AllowUntracked');

    return this.httpClient.post<unknown>(url, { Value: allowUntracked });
  }

  public isTransientClass(): boolean {
    return !!this.activeClassInfo && this.activeClassInfo.IsTransient;
  }

  public getPremiumSubscriptions(): Observable<PremiumSubscriptionsInfo> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'PremiumSubscriptions');

    return this.httpClient.get<PremiumSubscriptionsInfo>(url);
  }

  public upgradeWithCredit(classId: string): Observable<UpdatedClassInfo> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'Class', classId, 'UpgradeWithCredit');

    return this.httpClient.post<UpdatedClassInfo>(url, {});
  }

  public upgradeWithCode(classId: string, redemptionCode: string): Observable<UpdatedClassInfo> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'Class', classId, 'UpgradeWithCode');

    return this.httpClient.post<UpdatedClassInfo>(url, { Value: redemptionCode });
  }

  public getClassStudents(classId: string): Observable<ClassStudentModel[]> {
    const url = this.urlService.buildServerApiUrl('Teacher/Class', classId, 'Students');

    return this.httpClient.get<{ Students: ClassStudentModel[] }>(url)
      .pipe(map(({ Students }) => Students));
  }

  public getClasses(): Observable<ClassInfo[]> {
    return this.dataService.getTeacherClasses()
      .pipe(tap(classes => { this.classesService.setClasses(classes); }));
  }

  public renewClass(): void {
    this.modalService.displayModal<RenewClassModalComponent>(RenewClassModalComponent, {}, 'sm');
  }
}
